/**
 * Single source of user-facing error copy.
 *
 * Nothing technical ever reaches a user: raw backend text is replaced here (at the
 * source, in `authorizationService.js`) and scrubbed again before render (in
 * `components/common/notify.jsx`). Keep every string plain-English and actionable.
 */

export const SUPPORT_LINE = "If this keeps happening, please contact support";

export const ERROR_COPY = {
    /* Expected / user-actionable — plain message, auto-closes, no support reference. */
    VALIDATION: "Please check the highlighted fields and try again.",
    FORBIDDEN: "You don't have permission to do that.",
    NOT_FOUND: "We couldn't find what you were looking for.",
    SESSION: "Your session has expired. Please sign in again.",
    CLIENT: "We couldn't complete that request. Please try again.",
    /* CANCELED intentionally has no copy — the app abandoned the request. */

    /* Unexpected / our fault — stays until dismissed, shows a support reference. */
    TIMEOUT: "This is taking longer than usual. Please try again.",
    OFFLINE: "Can't reach the server. Please check your connection and try again.",
    UNAVAILABLE: "The service is temporarily unavailable. Please try again in a moment.",
    SERVER: "Something went wrong on our end. We've logged it and we're looking into it.",
    GENERIC: "Something went wrong. Please try again."
};

/**
 * Export failures. Separate from ERROR_COPY because these come from the response status and say
 * something a generic message would throw away. Logic lives in `getExportErrorMessage`.
 */
export const EXPORT_COPY = {
    NO_DATA: "There's no data to export for the current filters.",
    TEMPLATE_NOT_EXIST: "That template file is no longer available. Please upload a new template.",
    TEMPLATE_NOT_FOUND: "No template file was found. Please upload a template before exporting.",
    FAILED: "The export couldn't be completed. Please try again."
};

/**
 * Keys the user cannot act on: these get a persistent toast plus a copyable reference, and are
 * the only ones reported to Elasticsearch. Everything else — validation, permission, not-found,
 * expired session, failed login — is ordinary traffic, and reporting it would bury real faults.
 */
const UNEXPECTED_KEYS = ["TIMEOUT", "OFFLINE", "UNAVAILABLE", "SERVER", "GENERIC"];

export const isUnexpectedKey = key => UNEXPECTED_KEYS.includes(key);

/* The wording of those same keys. Lets the alert layer recognise an "our fault" message from its
   text alone, which is all a legacy call site passing a bare string can give it. */
export const UNEXPECTED_COPY = new Set(UNEXPECTED_KEYS.map(key => ERROR_COPY[key]).filter(Boolean));

/* Anything matching these is developer-facing and must never reach a user. Patterns are
   deliberately specific: a bare /50\d/ would false-positive on copy like "500 records
   updated", so we match axios' literal message text instead. */
const TECHNICAL_PATTERNS = [
    /internal server error/i,
    /request failed with status code/i,
    /\btraceback\b/i,
    /\bexception\b/i,
    /<!doctype/i,
    /<\/?html/i,
    /\bECONN[A-Z]+\b/,
    /\bETIMEDOUT\b/,
    /\bENOTFOUND\b/,
    /xmlhttprequest/i,
    /\[object Object\]/,
    /^\s*(error|null|undefined|nan)\s*$/i
];

export const isTechnicalMessage = message => {
    const text = typeof message === "string" ? message.trim() : "";
    if (!text || text.length > 300) return true;
    return TECHNICAL_PATTERNS.some(pattern => pattern.test(text));
};

/**
 * Last line of defence for the "no technical text, ever" guarantee. Applied when a message
 * is rendered, so it sits after every difference in backend field shape and after any call
 * site's hardcoded fallback — an unmigrated component reading `error.detail` directly still
 * cannot leak raw text.
 */
export const scrubMessage = message => (isTechnicalMessage(message) ? ERROR_COPY.GENERIC : message.trim());
